import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types';
import { fetchYanxuanSupplierCapability } from '../services/yanxuan-order.service';
import { dualRoutePath } from '../utils/workbenchRoutes';
import { detectMobileOrMiniProgram } from '../utils/supplierView';
import { isYanxuanEnabled } from '../config/yanxuanFeature';
import { YANXUAN_OPS_WORKBENCH_ROLES } from '../utils/workbenchView';
import YanxuanMonthlyAgreementModal from './yanxuan/YanxuanMonthlyAgreementModal';

type GateState =
  | { kind: 'loading' }
  | { kind: 'denied' }
  | { kind: 'need_agreement' }
  | { kind: 'allowed' };

/** 严选运营身份或平台指定严选供应商可进入 /yanxuan；渠道商须先同意月结三方协议 */
const YanxuanSellerWorkbenchGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useAuthStore((state) => state.user);
  const isOpsRole =
    !!user?.role &&
    YANXUAN_OPS_WORKBENCH_ROLES.includes(user.role as UserRole);
  const [state, setState] = useState<GateState>(() =>
    isYanxuanEnabled() && isOpsRole ? { kind: 'allowed' } : { kind: 'loading' },
  );

  const probe = useCallback(() => {
    if (!user || !isYanxuanEnabled()) {
      setState({ kind: 'denied' });
      return;
    }
    if (YANXUAN_OPS_WORKBENCH_ROLES.includes(user.role as UserRole)) {
      setState({ kind: 'allowed' });
      return;
    }
    if (user.role !== UserRole.SUPPLIER) {
      setState({ kind: 'denied' });
      return;
    }
    let active = true;
    setState({ kind: 'loading' });
    fetchYanxuanSupplierCapability()
      .then((capability) => {
        if (!active) return;
        if (!capability.is_channel_seller) {
          setState({ kind: 'denied' });
          return;
        }
        const agreement = capability.monthly_tripartite_agreement;
        if (agreement?.required && !agreement.accepted) {
          setState({ kind: 'need_agreement' });
          return;
        }
        setState({ kind: 'allowed' });
      })
      .catch(() => {
        if (active) setState({ kind: 'denied' });
      });
    return () => {
      active = false;
    };
  }, [user?.id, user?.role]);

  useEffect(() => {
    const cleanup = probe();
    return () => {
      cleanup?.();
    };
  }, [probe]);

  const supplierHome = dualRoutePath('supplier', detectMobileOrMiniProgram());

  if (state.kind === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (state.kind === 'denied') {
    return <Navigate to={supplierHome} replace />;
  }

  if (state.kind === 'need_agreement') {
    return (
      <>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" description="请先确认严选月结协议" />
        </div>
        <YanxuanMonthlyAgreementModal
          open
          cancelText="返回自选直采供应商工作台"
          onAccepted={() => setState({ kind: 'allowed' })}
          onCancel={() => setState({ kind: 'denied' })}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default YanxuanSellerWorkbenchGate;
